import { useEffect, useState, type FormEvent } from "react";
import type { ValorJus } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Campo, Input, TextArea } from "../../components/Formulario";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

// UI simplificada: el usuario solo ve "Valor JUS actual" + un botón
// "Editar". El historial completo (fechas de vigencia, cierres) sigue
// existiendo del lado del servidor — ver POST /valores-jus/reemplazar en
// functions/_app/routes/valores-jus.ts — pero es transparente para el
// usuario, tal como se pidió.
export function PanelValoresJus({ puedeAdministrar }: { puedeAdministrar: boolean }) {
  const { mostrar } = useToast();
  const [actual, setActual] = useState<ValorJus | null>(null);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [valores, setValores] = useState({ valor: "", fuente: "", observacion: "" });
  const [guardando, setGuardando] = useState(false);

  function cargar() {
    setCargando(true);
    api
      .get<{ item: ValorJus | null }>("/catalogos/valor-jus")
      .then((d) => setActual(d.item))
      .catch(() => mostrar("No se pudo cargar el valor JUS.", "error"))
      .finally(() => setCargando(false));
  }
  useEffect(cargar, []); // eslint-disable-line react-hooks/exhaustive-deps

  function abrirEdicion() {
    setValores({
      valor: actual ? String(actual.valor) : "",
      fuente: actual?.fuente ?? "",
      observacion: actual?.observacion ?? "",
    });
    setModalAbierto(true);
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.post("/valores-jus/reemplazar", {
        valor: Number(valores.valor),
        fuente: valores.fuente,
        observacion: valores.observacion || null,
      });
      mostrar("Valor JUS actualizado.", "exito");
      setModalAbierto(false);
      cargar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo actualizar el valor JUS.", "error");
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-texto">Valor JUS (Ley 9.131)</h3>
          <p className="text-xs text-texto-secundario">Usado por el motor de cálculo de honorarios de este estudio.</p>
        </div>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-borde bg-fondo p-4">
        {cargando ? (
          <p className="text-sm text-texto-secundario">Cargando…</p>
        ) : actual ? (
          <p className="text-lg font-semibold text-texto">Valor JUS actual: ${actual.valor.toLocaleString("es-AR")}</p>
        ) : (
          <p className="text-sm text-texto-secundario">Sin valor JUS configurado.</p>
        )}
        {puedeAdministrar && <Boton onClick={abrirEdicion}>Editar</Boton>}
      </div>

      <Modal titulo="Editar valor JUS" abierto={modalAbierto} onCerrar={() => setModalAbierto(false)}>
        <form onSubmit={guardar} className="space-y-4">
          <Campo etiqueta="Valor" obligatorio>
            <Input
              type="number"
              step="0.01"
              required
              value={valores.valor}
              onChange={(e) => setValores((v) => ({ ...v, valor: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Fuente" obligatorio>
            <Input
              required
              value={valores.fuente}
              onChange={(e) => setValores((v) => ({ ...v, fuente: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Observación">
            <TextArea
              rows={2}
              value={valores.observacion}
              onChange={(e) => setValores((v) => ({ ...v, observacion: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2 pt-2">
            <Boton type="button" variante="secundario" onClick={() => setModalAbierto(false)} disabled={guardando}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
